import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useCheckout } from "../../hooks/useCheckout";
import { useCart } from "../../contexts/CartContext";
import { useAuth } from "../../contexts/AuthContext";
import { fetchWalletData } from "../../store/slices/walletSlice";
import { updateCheckoutField as updateCheckoutFieldAction } from "../../store/slices/checkoutSlice";
import {
  fetchAvailableShippingMethods,
  selectShippingMethod,
} from "../../store/slices/shippingSlice";
import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Divider,
  Alert,
  Chip,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { formatCurrency } from "../../utils/formatters";
import CountrySelect from "../common/CountrySelect";
import CouponInput from "./CouponInput";
import { calculateTax } from "../../store/slices/taxSlice";

/**
 * Component for the Address & Shipping step in the checkout process.
 * Handles input for shipping/billing addresses, shipping method, payment method, coupon code,
 * and applying wallet balance.
 */
const AddressStep = ({ onWalletAmountChange }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { t: tCheckout } = useTranslation("checkout");
  const { updateCheckoutField, handleSameAsShipping } = useCheckout();
  const { cart } = useCart();
  const { user } = useAuth();
  const checkoutData = useSelector((state) => state.checkout.checkoutData);
  const { wallet, status: walletStatus } = useSelector((state) => state.wallet);
  const { taxRate, isLoading: taxLoading } = useSelector((state) => state.tax);
  const {
    availableShippingMethods,
    selectedMethod,
    status: shippingStatus,
    error: shippingError,
  } = useSelector((state) => state.shipping);

  // Local state for wallet checkbox
  const [useWalletChecked, setUseWalletChecked] = useState(false);
  // Local state for wallet application UI
  const [amountToApplyFromWallet, setAmountToApplyFromWallet] = useState(0);
  const [amountDue, setAmountDue] = useState(0);

  // Fetch wallet data
  useEffect(() => {
    if (walletStatus === "idle") {
      dispatch(fetchWalletData());
    }
  }, [dispatch, walletStatus]);

  // Fetch shipping methods when cart total changes
  useEffect(() => {
    if (cart?.subtotal) {
      dispatch(fetchAvailableShippingMethods(cart.subtotal));
    }
  }, [dispatch, cart?.subtotal, cart?.items?.length]);

  // When shipping methods are loaded or selection changes, update checkout field
  useEffect(() => {
    if (selectedMethod) {
      dispatch(
        updateCheckoutFieldAction({
          field: "shippingMethodId",
          value: selectedMethod.id,
        })
      );
    }
  }, [dispatch, selectedMethod]);

  // Listen for cart update events from other components
  useEffect(() => {
    const handleCartUpdate = (event) => {
      const { subtotal } = event.detail;
      // If the subtotal has changed, refetch shipping methods
      if (subtotal !== undefined) {
        dispatch(fetchAvailableShippingMethods(subtotal));
      }
    };

    window.addEventListener("cart-updated", handleCartUpdate);

    return () => {
      window.removeEventListener("cart-updated", handleCartUpdate);
    };
  }, [dispatch]);

  // Calculate totals using cart from context and shipping from selected method
  const { subtotal, tax, shippingCost, discount, total } = useMemo(() => {
    // Use actual cart totals from cart context
    const cartSubtotal = parseFloat(cart?.subtotal || 0);
    const cartDiscount = parseFloat(cart?.discount || 0);

    // Calculate shipping based on selected method
    let calculatedShipping = 0;
    if (selectedMethod) {
      calculatedShipping = parseFloat(selectedMethod.price || 0);
    }

    // Get tax amount from cart or calculate based on the tax rate
    // Priority: 1. Cart tax (if available) 2. Calculate with taxRate from redux 3. Default to 0
    const cartTax =
      cart?.tax !== undefined
        ? parseFloat(cart.tax)
        : parseFloat(
            ((cartSubtotal - cartDiscount) * (taxRate?.rate || 0.077)).toFixed(
              2
            )
          );

    // Calculate total
    const cartTotal =
      cartSubtotal + calculatedShipping + cartTax - cartDiscount;

    return {
      subtotal: cartSubtotal,
      tax: cartTax,
      shippingCost: calculatedShipping,
      discount: cartDiscount,
      total: cartTotal,
    };
  }, [cart, selectedMethod, taxRate]);

  // Force recalculation when selected shipping method changes
  useEffect(() => {
    // This will trigger a re-render with updated shipping cost when the selectedMethod changes
    if (selectedMethod) {
      console.log(
        "Shipping method updated:",
        selectedMethod.name,
        "Price:",
        selectedMethod.price
      );
    }
  }, [selectedMethod]);

  // Fetch tax information when subtotal or shipping address changes
  useEffect(() => {
    if (cart?.subtotal && checkoutData?.shippingAddress?.countryCode) {
      // Calculate tax based on subtotal and shipping address
      dispatch(
        calculateTax({
          subtotal: cart.subtotal - (cart.discount || 0),
          countryCode: checkoutData.shippingAddress.countryCode,
          stateCode: checkoutData.shippingAddress.state,
          zipCode: checkoutData.shippingAddress.zipCode,
        })
      );
    }
  }, [dispatch, cart?.subtotal, cart?.discount, checkoutData?.shippingAddress]);

  // Recalculate wallet application and amount due
  useEffect(() => {
    const walletBalance = wallet?.balance ? parseFloat(wallet.balance) : 0;
    // Use the total calculated in the useMemo hook above
    const currentTotal = total;
    let applyAmount = 0;

    if (useWalletChecked && walletBalance > 0 && currentTotal > 0) {
      applyAmount = Math.min(walletBalance, currentTotal);
      // Ensure precision after calculation
      applyAmount = parseFloat(Math.max(0, applyAmount).toFixed(2));
    } else {
      // Ensure applyAmount is 0 if checkbox is unchecked or balance/total is zero
      applyAmount = 0;
    }

    const dueAmount = parseFloat(
      Math.max(0, currentTotal - applyAmount).toFixed(2)
    );

    // Update local state for immediate UI feedback
    setAmountToApplyFromWallet(applyAmount);
    setAmountDue(dueAmount);

    // Only dispatch if the calculated amount is different from what's in Redux
    // This prevents unnecessary dispatches and potential re-renders
    if (checkoutData.amountToUseFromWallet !== applyAmount) {
      dispatch(
        updateCheckoutFieldAction({
          field: "amountToUseFromWallet",
          value: applyAmount,
        })
      );
    }

    // Notify parent component (CheckoutPage) about the change
    if (typeof onWalletAmountChange === "function") {
      onWalletAmountChange(applyAmount);
    }

    // Dependencies: Checkbox state, wallet object (for balance), calculated total,
    // dispatch function, parent callback, and the value currently in Redux state.
  }, [
    useWalletChecked,
    wallet,
    total,
    dispatch,
    onWalletAmountChange,
    checkoutData.amountToUseFromWallet, // Add this dependency
  ]);

  // Determine if payment methods should be disabled
  const isWalletCoveringFullAmount = amountDue <= 0.009 && useWalletChecked;

  // Determine if addresses were pre-filled from profile (for informational alert)
  const shippingPrefilled = useMemo(() => {
    return (
      user?.shippingAddress &&
      user?.shippingCity &&
      user?.shippingPostalCode &&
      checkoutData.shippingAddress.address === user.shippingAddress &&
      checkoutData.shippingAddress.city === user.shippingCity &&
      checkoutData.shippingAddress.postalCode === user.shippingPostalCode
    );
  }, [user, checkoutData.shippingAddress]);

  // Check if billing address was prefilled
  const billingPrefilled = useMemo(() => {
    return (
      !checkoutData.billingAddress.sameAsShipping &&
      user?.billingAddress &&
      user?.billingCity &&
      user?.billingPostalCode &&
      checkoutData.billingAddress.address === user.billingAddress &&
      checkoutData.billingAddress.city === user.billingCity &&
      checkoutData.billingAddress.postalCode === user.billingPostalCode
    );
  }, [user, checkoutData.billingAddress]);

  const handleFieldChange = (section, field, value) => {
    updateCheckoutField(section, field, value);
  };

  const handleWalletCheckboxChange = (event) => {
    setUseWalletChecked(event.target.checked);
  };

  // Handler for selecting a shipping method
  const handleShippingMethodChange = (event) => {
    const selectedId = event.target.value;
    const method = availableShippingMethods.find((m) => m.id === selectedId);
    if (method) {
      dispatch(selectShippingMethod(method));
    }
  };

  return (
    <Box>
      {/* --- Shipping Address --- */}
      <Typography variant="h6" gutterBottom>
        {tCheckout("shippingAddress")}
      </Typography>

      {shippingPrefilled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {tCheckout("shippingAddressPrefilled")}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="shippingAddress.address"
            label={tCheckout("address")}
            value={checkoutData.shippingAddress.address || ""}
            onChange={(e) =>
              handleFieldChange("shippingAddress", "address", e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="shippingAddress.city"
            label={tCheckout("city")}
            value={checkoutData.shippingAddress.city || ""}
            onChange={(e) =>
              handleFieldChange("shippingAddress", "city", e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="shippingAddress.postalCode"
            label={tCheckout("postalCode")}
            value={checkoutData.shippingAddress.postalCode || ""}
            onChange={(e) =>
              handleFieldChange("shippingAddress", "postalCode", e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12}>
          <CountrySelect
            label={tCheckout("country")}
            required
            value={checkoutData.shippingAddress.country || ""}
            onChange={(value) =>
              handleFieldChange("shippingAddress", "country", value)
            }
          />
        </Grid>
      </Grid>

      {/* --- Billing Address --- */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" gutterBottom>
        {tCheckout("billingAddress")}
      </Typography>

      {billingPrefilled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {tCheckout("billingAddressPrefilled")}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={checkoutData.billingAddress.sameAsShipping}
            onChange={(e) => handleSameAsShipping(e.target.checked)}
            name="sameAsShipping"
          />
        }
        label={tCheckout("sameAsShippingAddress")}
      />

      {!checkoutData.billingAddress.sameAsShipping && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="billingAddress.address"
              label={tCheckout("address")}
              value={checkoutData.billingAddress.address || ""}
              onChange={(e) =>
                handleFieldChange("billingAddress", "address", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="billingAddress.city"
              label={tCheckout("city")}
              value={checkoutData.billingAddress.city || ""}
              onChange={(e) =>
                handleFieldChange("billingAddress", "city", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="billingAddress.postalCode"
              label={tCheckout("postalCode")}
              value={checkoutData.billingAddress.postalCode || ""}
              onChange={(e) =>
                handleFieldChange(
                  "billingAddress",
                  "postalCode",
                  e.target.value
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <CountrySelect
              label={tCheckout("country")}
              required
              value={checkoutData.billingAddress.country || ""}
              onChange={(value) =>
                handleFieldChange("billingAddress", "country", value)
              }
            />
          </Grid>
        </Grid>
      )}

      {/* --- Shipping Method --- */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" gutterBottom>
        {tCheckout("shippingMethod")}
      </Typography>

      {shippingStatus === "loading" && (
        <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">
            {tCheckout("loadingShippingMethods")}
          </Typography>
        </Box>
      )}

      {shippingStatus === "failed" && (
        <Alert severity="error" sx={{ my: 2 }}>
          {shippingError || tCheckout("errorLoadingShippingMethods")}
        </Alert>
      )}

      {shippingStatus === "succeeded" &&
        availableShippingMethods.length === 0 && (
          <Alert severity="warning" sx={{ my: 2 }}>
            {tCheckout("noShippingMethodsAvailable")}
          </Alert>
        )}

      {shippingStatus === "succeeded" &&
        availableShippingMethods.length > 0 && (
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="shipping-method"
              name="shippingMethod"
              value={selectedMethod?.id || ""}
              onChange={handleShippingMethodChange}
            >
              {availableShippingMethods.map((method) => (
                <FormControlLabel
                  key={method.id}
                  value={method.id}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        {method.name} - {formatCurrency(method.price)}
                        {method.freeShippingApplied && (
                          <Chip
                            label={tCheckout("freeShipping")}
                            color="success"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      {method.estimatedDeliveryTime && (
                        <Typography variant="body2" color="text.secondary">
                          {tCheckout("estimatedDelivery")}:{" "}
                          {method.estimatedDeliveryTime}
                        </Typography>
                      )}
                      {method.description && (
                        <Typography variant="body2" color="text.secondary">
                          {method.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}

      {/* --- Wallet Balance & Application --- */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" gutterBottom>
        {tCheckout("walletAndPayment")}
      </Typography>
      {walletStatus === "loading" && (
        <CircularProgress size={20} sx={{ mr: 1 }} />
      )}
      {wallet && wallet.balance > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
            boxShadow: 1,
          }}
        >
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            {tCheckout("availableWalletBalance")}:{" "}
            <span style={{ color: "#2e7d32" }}>
              {formatCurrency(wallet.balance, wallet.currency)}
            </span>
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={useWalletChecked}
                onChange={handleWalletCheckboxChange}
                name="useWallet"
                color="primary"
              />
            }
            label={tCheckout("useWalletBalance", {
              amount: formatCurrency(
                Math.min(wallet.balance, total),
                wallet.currency
              ),
            })}
          />
          {useWalletChecked && (
            <Box mt={1}>
              <Chip
                label={tCheckout("applyingFromWallet", {
                  applyAmount: formatCurrency(
                    amountToApplyFromWallet,
                    wallet.currency
                  ),
                  dueAmount: formatCurrency(amountDue, wallet.currency),
                })}
                color={isWalletCoveringFullAmount ? "success" : "info"}
                size="medium"
                sx={{ fontWeight: "bold" }}
              />
            </Box>
          )}
        </Box>
      )}
      {walletStatus === "failed" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {tCheckout("walletLoadError")}
        </Alert>
      )}

      {/* --- Payment Method --- */}
      <FormControl component="fieldset" disabled={isWalletCoveringFullAmount}>
        <FormLabel component="legend">{tCheckout("paymentMethod")}</FormLabel>
        <RadioGroup
          row
          aria-label="payment-method"
          name="paymentMethod"
          value={checkoutData.paymentMethod}
          onChange={(e) =>
            handleFieldChange(null, "paymentMethod", e.target.value)
          }
        >
          <FormControlLabel
            value="credit_card"
            control={<Radio />}
            label={tCheckout("creditCard")}
            disabled={isWalletCoveringFullAmount}
          />
          {/* Hide Stripe Bank Transfer for now */}
          {/* <FormControlLabel
            value="stripe_bank_transfer"
            control={<Radio />}
            label="Bank Transfer (Stripe)" 
          /> */}
          <FormControlLabel
            value="bank_transfer_manual"
            control={<Radio />}
            label={tCheckout("bankTransferManual")}
            disabled={isWalletCoveringFullAmount}
          />
          <FormControlLabel
            value="twint"
            control={<Radio />}
            label={tCheckout("twint")}
            disabled={isWalletCoveringFullAmount}
          />
        </RadioGroup>
        {isWalletCoveringFullAmount && (
          <FormHelperText sx={{ color: "success.main", fontWeight: "bold" }}>
            {tCheckout("walletCoversFullAmount")}
          </FormHelperText>
        )}
      </FormControl>

      {/* --- Discount Code --- */}
      <Divider sx={{ my: 3 }} />
      <CouponInput />
    </Box>
  );
};

AddressStep.defaultProps = {
  onWalletAmountChange: null,
};

export default AddressStep;
